import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Layout from '../layout/Layout';
import SkillsSelector from './SkillsSelector';
import { useCompanyFilter } from '../../hooks/useCompanyFilter';
import { useToast } from '../../context/ToastContext';
import { usePermissions, PERMISSIONS } from '../../context/PermissionsContext';
import { getCookie } from '../../utils/cookies';
import { generateJobDescription, generateJobBenefits, getAssetUrl } from '../../services/api';

const CreateCircular = () => {
    const navigate = useNavigate();
    const { selectedCompany } = useCompanyFilter();
    const toast = useToast();
    const { hasPermission } = usePermissions();
    const [step, setStep] = useState(1);
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [designations, setDesignations] = useState([]);
    const [isCreatingRole, setIsCreatingRole] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
    const [isGeneratingBenefits, setIsGeneratingBenefits] = useState(false);

    const handleGenerateDescription = async () => {
        if (!formData.title && !formData.role) {
            toast.showToast("Please enter a job title or select a designation level first.", "error");
            return;
        }
        setIsGeneratingDesc(true);
        try {
            const response = await generateJobDescription({
                title: formData.title,
                role: formData.role,
                experience: formData.experience,
                jobNature: formData.jobNature,
                companyName: selectedCompany?.name
            });
            setFormData(prev => ({ ...prev, description: response.data.description }));
            toast.showToast("Job description generated with AI", "success");
        } catch (error) {
            console.error("Error generating job description:", error);
            if (error.response?.status === 403 || error.response?.data?.error === 'PREMIUM_FEATURE_RESTRICTED') {
                window.dispatchEvent(new CustomEvent('open-upgrade-modal', { detail: { featureKey: 'ai' } }));
            } else {
                toast.showToast("Failed to generate job description", "error");
            }
        } finally {
            setIsGeneratingDesc(false);
        }
    };

    const handleGenerateBenefits = async () => {
        if (!formData.title && !formData.role) {
            toast.showToast("Please enter a job title or select a designation level first.", "error");
            return;
        }
        setIsGeneratingBenefits(true);
        try {
            const response = await generateJobBenefits({
                title: formData.title,
                role: formData.role,
                jobNature: formData.jobNature,
                companyName: selectedCompany?.name
            });
            setFormData(prev => ({ ...prev, benefits: response.data.benefits }));
            toast.showToast("Perks & benefits generated with AI", "success");
        } catch (error) {
            console.error("Error generating benefits:", error);
            if (error.response?.status === 403 || error.response?.data?.error === 'PREMIUM_FEATURE_RESTRICTED') {
                window.dispatchEvent(new CustomEvent('open-upgrade-modal', { detail: { featureKey: 'ai' } }));
            } else {
                toast.showToast("Failed to generate perks & benefits", "error");
            }
        } finally {
            setIsGeneratingBenefits(false);
        }
    };


    const canManageRecruitment = hasPermission(PERMISSIONS.MANAGE_RECRUITMENT);

    useEffect(() => {
        if (!canManageRecruitment && selectedCompany?.id !== 'personal') {
            toast.showToast('You do not have permission to manage recruitment', 'error');
            navigate('/recruitment');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManageRecruitment, navigate, selectedCompany]);

    const [formData, setFormData] = useState({
        title: '',
        role: '',
        salaryRange: { min: '', max: '' },
        experience: 0,
        description: '',
        jobNature: 'on-site',
        location: '',
        benefits: '',
        mandatorySkills: [],
        niceToHaveSkills: [],
        questions: [],
        deadline: ''
    });

    useEffect(() => {
        if (selectedCompany?.id && selectedCompany.id !== 'personal') {
            fetchDesignations();
            // Pre-fill location if company address exists
            if (selectedCompany.address) {
                setFormData(prev => ({ ...prev, location: selectedCompany.address }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany]);

    const fetchDesignations = async () => {
        try {
            const token = getCookie('authToken');
            const res = await axios.get(`/api/companies/${selectedCompany.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDesignations(res.data.designations || []);
        } catch (error) {
            console.error('Error fetching designations:', error);
        }
    };

    const handleCreateRole = async () => {
        if (!newRoleName.trim()) return;
        try {
            const token = getCookie('authToken');
            const res = await axios.post(`/api/companies/${selectedCompany.id}/designations`, {
                name: newRoleName.trim(),
                description: '',
                permissions: {
                    addEmployee: false,
                    viewEmployeeList: true,
                    editEmployee: false,
                    createDesignation: false,
                    viewDesignations: true,
                    editDesignation: false,
                    deleteDesignation: false,
                    createProject: false,
                    editProject: false,
                    deleteProject: false,
                    assignEmployeeToProject: false,
                    removeEmployeeFromProject: false,
                    viewProjectAnalytics: false,
                    createTask: false,
                    editTask: false,
                    deleteTask: false,
                    manageRecruitment: false
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setDesignations(res.data.designations || []);
            setFormData({ ...formData, role: newRoleName.trim() });
            setIsCreatingRole(false);
            setNewRoleName('');
            toast.showToast('Level created successfully', 'success');
        } catch (error) {
            toast.showToast(error.response?.data?.message || 'Failed to create level', 'error');
        }
    };

    const addQuestion = () => {
        setFormData({
            ...formData,
            questions: [...formData.questions, { question: '', type: 'text', required: true, options: [] }]
        });
    };

    const handleSubmit = async () => {
        try {
            const token = getCookie('authToken');
            // Clean up empty options before submission
            const cleanedFormData = {
                ...formData,
                questions: formData.questions.map(q => ({
                    ...q,
                    options: q.options.filter(o => o.trim() !== '')
                }))
            };
            const res = await axios.post('/api/recruitment/circulars', cleanedFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (coverImageFile) {
                const imageForm = new FormData();
                imageForm.append('image', coverImageFile);
                await axios.post(`/api/recruitment/circulars/${res.data._id}/image`, imageForm, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            toast.showToast('Job circular published successfully', 'success');
            navigate('/recruitment');
        } catch (error) {
            toast.showToast(error.response?.data?.message || 'Failed to publish circular', 'error');
        }
    };

    const steps = [
        { id: 1, label: 'Job Info' },
        { id: 2, label: 'Content' },
        { id: 3, label: 'Assessment' }
    ];

    return (
        <Layout>
            <div className="space-y-8 animate-in fade-in pb-20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Create Job Circular</h1>
                        <p className="text-slate-400 text-sm font-medium mt-1">Hiring for {selectedCompany?.name || 'Offtix Organization'}</p>
                    </div>

                    <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-200 shrink-0">
                        {steps.map(s => (
                            <button
                                key={s.id}
                                onClick={() => setStep(s.id)}
                                className={`px-5 py-2 rounded-lg text-xs font-bold transition-all ${step === s.id ? 'bg-white text-indigo-600 shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Form Section */}
                    <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl shadow-sm p-8 space-y-8">
                        {step === 1 && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Internal Job Title</label>
                                        <input
                                            type="text"
                                            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl focus:ring-4 focus:ring-indigo-100 outline-none transition-all font-bold text-slate-700 shadow-inner text-sm"
                                            placeholder="e.g. Senior Frontend Engineer"
                                            value={formData.title}
                                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Designation Level</label>
                                        {isCreatingRole ? (
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    className="flex-1 bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold text-slate-700 shadow-inner text-sm"
                                                    placeholder="New Level..."
                                                    value={newRoleName}
                                                    onChange={e => setNewRoleName(e.target.value)}
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    className="px-4 bg-indigo-600 text-white rounded-xl text-xs font-bold shadow-md hover:bg-indigo-700 transition-all disabled:opacity-50"
                                                    onClick={handleCreateRole}
                                                    disabled={!newRoleName.trim()}
                                                >Save</button>
                                                <button
                                                    type="button"
                                                    className="px-3 bg-white border border-slate-200 text-slate-500 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all"
                                                    onClick={() => {
                                                        setIsCreatingRole(false);
                                                        setNewRoleName('');
                                                    }}
                                                >×</button>
                                            </div>
                                        ) : (
                                            <select
                                                className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold text-slate-700 text-sm"
                                                value={formData.role}
                                                onChange={e => {
                                                    if (e.target.value === 'CREATE_NEW') {
                                                        setIsCreatingRole(true);
                                                    } else {
                                                        setFormData({ ...formData, role: e.target.value });
                                                    }
                                                }}
                                            >
                                                <option value="">Select Level</option>
                                                {designations.map(d => <option key={d._id} value={d.name}>{d.name}</option>)}
                                                <option value="CREATE_NEW" className="text-indigo-600 font-bold bg-indigo-50">+ Create New Level</option>
                                            </select>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Job Nature</label>
                                        <select
                                            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold text-slate-700 text-sm"
                                            value={formData.jobNature}
                                            onChange={e => setFormData({ ...formData, jobNature: e.target.value })}
                                        >
                                            <option value="remote">Remote</option>
                                            <option value="on-site">On-Site</option>
                                            <option value="hybrid">Hybrid</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Workplace Address</label>
                                        <textarea
                                            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold text-slate-700 shadow-inner min-h-[80px] resize-none text-sm"
                                            placeholder="Full Address / Venue Details..."
                                            value={formData.location}
                                            onChange={e => setFormData({ ...formData, location: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Salary Range & Payment Frequency</label>
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Currency</label>
                                                <select
                                                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl outline-none font-bold text-slate-700 text-xs"
                                                    value={formData.salaryRange?.currency || 'USD'}
                                                    onChange={e => setFormData({ ...formData, salaryRange: { ...formData.salaryRange, currency: e.target.value } })}
                                                >
                                                    <option value="USD">USD ($)</option>
                                                    <option value="BDT">BDT (৳)</option>
                                                    <option value="EUR">EUR (€)</option>
                                                    <option value="GBP">GBP (£)</option>
                                                    <option value="CAD">CAD ($)</option>
                                                    <option value="AUD">AUD ($)</option>
                                                </select>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Pay Period / Frequency</label>
                                                <select
                                                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl outline-none font-bold text-slate-700 text-xs"
                                                    value={formData.salaryRange?.period || 'yearly'}
                                                    onChange={e => setFormData({ ...formData, salaryRange: { ...formData.salaryRange, period: e.target.value } })}
                                                >
                                                    <option value="yearly">Per Year / Annual</option>
                                                    <option value="monthly">Per Month</option>
                                                    <option value="hourly">Per Hour</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Minimum Amount</label>
                                                <input
                                                    type="number"
                                                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl outline-none font-bold shadow-xs text-xs text-slate-800"
                                                    placeholder="Min e.g. 50000"
                                                    value={formData.salaryRange?.min || ''}
                                                    onChange={e => setFormData({ ...formData, salaryRange: { ...formData.salaryRange, min: e.target.value } })}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Maximum Amount</label>
                                                <input
                                                    type="number"
                                                    className="w-full bg-white border border-slate-200 p-2.5 rounded-xl outline-none font-bold shadow-xs text-xs text-slate-800"
                                                    placeholder="Max e.g. 75000"
                                                    value={formData.salaryRange?.max || ''}
                                                    onChange={e => setFormData({ ...formData, salaryRange: { ...formData.salaryRange, max: e.target.value } })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Min Experience (Years)</label>
                                    <input
                                        type="number"
                                        className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold shadow-inner text-sm"
                                        value={formData.experience}
                                        onChange={e => setFormData({ ...formData, experience: Number(e.target.value) })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Deadline</label>
                                        <input
                                            type="date"
                                            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold shadow-inner text-sm text-slate-700"
                                            value={formData.deadline}
                                            onChange={e => setFormData({ ...formData, deadline: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Cover Image</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="w-full bg-slate-50 border border-slate-200 p-2.5 rounded-xl outline-none font-bold shadow-inner text-sm text-slate-700 file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100"
                                            onChange={e => setCoverImageFile(e.target.files[0])}
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-6 border-t border-slate-50">
                                    <button onClick={() => setStep(2)} className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl text-[10px] uppercase tracking-[0.2em] shadow-xl hover:bg-slate-800 transition-all">Continue to Content</button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-8 animate-in fade-in">
                                <div className="grid grid-cols-1 gap-12">
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest ml-1">Job Description</label>
                                            <button
                                                type="button"
                                                onClick={handleGenerateDescription}
                                                disabled={isGeneratingDesc}
                                                className="text-xs font-bold text-indigo-600 flex items-center gap-1 hover:text-indigo-800 disabled:opacity-50 transition-all"
                                            >
                                                <span>✨</span>
                                                {isGeneratingDesc ? 'Generating...' : 'Write with AI'}
                                            </button>
                                        </div>
                                        <div className="h-64 mb-12">
                                            <ReactQuill theme="snow" value={formData.description} onChange={val => setFormData({ ...formData, description: val })} className="h-48" />
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest ml-1">Perks & Benefits</label>
                                            <button
                                                type="button"
                                                onClick={handleGenerateBenefits}
                                                disabled={isGeneratingBenefits}
                                                className="text-xs font-bold text-indigo-600 flex items-center gap-1 hover:text-indigo-800 disabled:opacity-50 transition-all"
                                            >
                                                <span>✨</span>
                                                {isGeneratingBenefits ? 'Generating...' : 'Write with AI'}
                                            </button>
                                        </div>
                                        <div className="h-64 mb-12">
                                            <ReactQuill theme="snow" placeholder="Healthcare, Bonuses, Flexible hours..." value={formData.benefits} onChange={val => setFormData({ ...formData, benefits: val })} className="h-48" />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-16 border-t border-slate-50">
                                    <SkillsSelector label="Mandatory Skills" selectedSkills={formData.mandatorySkills} setSelectedSkills={skills => setFormData({ ...formData, mandatorySkills: skills })} />
                                    <SkillsSelector label="Nice to Have" selectedSkills={formData.niceToHaveSkills} setSelectedSkills={skills => setFormData({ ...formData, niceToHaveSkills: skills })} />
                                </div>
                                <div className="flex gap-4 pt-10">
                                    <button onClick={() => setStep(1)} className="px-10 py-4 bg-slate-50 text-slate-500 rounded-2xl font-bold text-[10px] uppercase tracking-widest border border-slate-100">Back</button>
                                    <button onClick={() => setStep(3)} className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-bold text-[10px] uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-xl">Review & Screening</button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="space-y-10 animate-in fade-in">
                                <div className="flex items-center justify-between border-b border-slate-100 pb-6">
                                    <div className="space-y-1">
                                        <h3 className="font-bold text-slate-900">Screening Questions</h3>
                                        <p className="text-xs text-slate-400 font-medium italic">Add questions to pre-filter candidates</p>
                                    </div>
                                    <button
                                        onClick={addQuestion}
                                        className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all border border-indigo-100"
                                    >+ New</button>
                                </div>
                                <div className="grid gap-6">
                                    {formData.questions.map((q, i) => (
                                        <div key={i} className="p-8 bg-slate-50 rounded-3xl border border-slate-100 space-y-6 relative group border-l-[6px] border-l-indigo-400 hover:border-l-indigo-600 transition-all">
                                            <button
                                                onClick={() => setFormData({ ...formData, questions: formData.questions.filter((_, idx) => idx !== i) })}
                                                className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-rose-500 rounded-lg hover:bg-rose-50 shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                                            >×</button>

                                            <div className="space-y-3">
                                                <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Question Text</label>
                                                <input
                                                    type="text"
                                                    className="w-full bg-white border border-slate-200 p-4 rounded-2xl outline-none font-bold text-slate-700 shadow-inner"
                                                    placeholder="Ask something..."
                                                    value={q.question}
                                                    onChange={e => {
                                                        const qs = [...formData.questions];
                                                        qs[i].question = e.target.value;
                                                        setFormData({ ...formData, questions: qs });
                                                    }}
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-6">
                                                <div className="space-y-3">
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Answer Type</label>
                                                    <select
                                                        className="w-full bg-white border border-slate-200 p-3 rounded-xl font-bold text-[10px] uppercase text-slate-500 outline-none"
                                                        value={q.type}
                                                        onChange={e => {
                                                            const qs = [...formData.questions];
                                                            qs[i].type = e.target.value;
                                                            setFormData({ ...formData, questions: qs });
                                                        }}
                                                    >
                                                        <option value="text">Short Input</option>
                                                        <option value="long-text">Long Answer</option>
                                                        <option value="selection">Selection List</option>
                                                        <option value="radio">Radio Buttons</option>
                                                        <option value="checkbox">Checkboxes</option>
                                                    </select>
                                                </div>
                                                <div className="space-y-3">
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Requirement</label>
                                                    <div className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl">
                                                        <input type="checkbox" className="w-4 h-4 rounded-md text-indigo-600" checked={q.required} onChange={e => {
                                                            const qs = [...formData.questions];
                                                            qs[i].required = e.target.checked;
                                                            setFormData({ ...formData, questions: qs });
                                                        }} />
                                                        <span className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Mandatory</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {['selection', 'radio', 'checkbox'].includes(q.type) && (
                                                <div className="space-y-4 animate-in slide-in-from-top-2">
                                                    <div className="flex items-center justify-between">
                                                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Options</label>
                                                        <button
                                                            onClick={() => {
                                                                const qs = [...formData.questions];
                                                                qs[i].options = [...qs[i].options, ''];
                                                                setFormData({ ...formData, questions: qs });
                                                            }}
                                                            className="text-[9px] font-black uppercase text-indigo-600 hover:text-indigo-700 underline tracking-widest"
                                                        >+ Add Option</button>
                                                    </div>
                                                    <div className="grid gap-3">
                                                        {q.options.map((opt, optIdx) => (
                                                            <div key={optIdx} className="flex gap-2">
                                                                <input
                                                                    type="text"
                                                                    className="flex-1 bg-white border border-slate-200 p-3 rounded-xl outline-none font-bold text-slate-700 shadow-inner text-sm"
                                                                    placeholder={`Option ${optIdx + 1}`}
                                                                    value={opt}
                                                                    onChange={e => {
                                                                        const qs = [...formData.questions];
                                                                        qs[i].options[optIdx] = e.target.value;
                                                                        setFormData({ ...formData, questions: qs });
                                                                    }}
                                                                />
                                                                <button
                                                                    onClick={() => {
                                                                        const qs = [...formData.questions];
                                                                        qs[i].options = qs[i].options.filter((_, idx) => idx !== optIdx);
                                                                        setFormData({ ...formData, questions: qs });
                                                                    }}
                                                                    className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                                                                >🗑️</button>
                                                            </div>
                                                        ))}
                                                        {q.options.length === 0 && (
                                                            <p className="text-[10px] text-slate-400 italic">No options added. Click 'Add Option' to begin.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-4 pt-10 border-t border-slate-100">
                                    <button onClick={() => setStep(2)} className="px-10 py-4 bg-slate-50 text-slate-500 rounded-xl font-bold text-xs">Back</button>
                                    <button onClick={handleSubmit} className="flex-1 bg-indigo-600 text-white py-4 rounded-xl font-bold text-xs shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all">Publish Circular</button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Simple Preview Card */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="sticky top-10 space-y-6">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Live Preview</p>
                            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 rounded-xl border border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50 shrink-0">
                                            {selectedCompany?.logo ? (
                                                <img src={getAssetUrl(selectedCompany.logo)} alt="Logo" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-xl font-black text-indigo-600">{(selectedCompany?.name || formData.title || 'O').charAt(0)}</span>
                                            )}
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-slate-900">{formData.title || 'Job Title'}</h2>
                                            <div className="flex items-center gap-1.5 mt-1 text-[13px] text-slate-500 font-medium flex-wrap">
                                                <span className="text-slate-800 font-bold">{selectedCompany?.name || 'Company Name'}</span>
                                                <span className="text-slate-300">•</span>
                                                <span className="flex items-center gap-1">
                                                    <span className="text-rose-500">📍</span> {formData.location || 'Location'}
                                                </span>
                                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[11px] font-bold ml-1 capitalize">
                                                    {formData.jobNature || 'Job Nature'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-slate-50 transition-colors shrink-0">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                                    </button>
                                </div>

                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="px-3 py-1.5 bg-emerald-50/50 text-emerald-700 font-bold text-xs border border-emerald-100 rounded-lg flex items-center gap-1.5">
                                        <span>💰</span> {(() => {
                                            const symbolMap = { USD: '$', BDT: '৳', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$' };
                                            const symbol = symbolMap[formData.salaryRange?.currency] || '৳';
                                            return `${symbol}${Number(formData.salaryRange?.min || 0).toLocaleString()} - ${symbol}${Number(formData.salaryRange?.max || 0).toLocaleString()} / ${formData.salaryRange?.period === 'yearly' ? 'yr' : 'mo'}`;
                                        })()}
                                    </div>
                                    <div className="px-3 py-1.5 bg-slate-50 text-slate-700 font-bold text-xs border border-slate-200 rounded-lg flex items-center gap-1.5">
                                        <span className="text-amber-500">⚡</span> {formData.experience || 0} yrs exp
                                    </div>
                                </div>

                                <div className="text-[13px] text-slate-600 leading-relaxed line-clamp-2">
                                    <span className="font-bold text-slate-700">Role Summary</span> {formData.description ? formData.description.replace(/<[^>]+>/g, '') : 'No description provided yet.'}
                                </div>

                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-[13px] font-medium text-slate-500">
                                        <span>Posted just now</span>
                                        <span className="text-slate-300">•</span>
                                        <span>Actively Recruiting</span>
                                    </div>
                                    <div className="text-[13px] font-bold text-indigo-600 flex items-center gap-1">
                                        View Role <span>→</span>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-slate-50">
                                    <button className="w-full py-3 bg-slate-900 text-white rounded-full text-xs font-bold shadow-lg hover:bg-slate-800 transition-all">Preview Full Detail</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CreateCircular;
